import React from 'react';
import { Checkbox, Popover } from '@brighterion/br-ui';
import { Field, FieldProps, FieldArrayRenderProps } from 'formik';
import { useTranslation } from 'react-i18next';
import useGetSortedLists from 'Hooks/query-hooks/global-rules/useGetSortedLists';
import OperatorField from './OperatorField';
import ParameterField from './ParameterField';
import ValueField from './ValueField';
import styles from './Condition.css';

type ConditionProps = {
    condition: CustomConditionObj;
    index: number;
    isThresholdEditorOnly: boolean;
    arrayHelper: FieldArrayRenderProps;
    overridesExist?: boolean;
    parameterData: UseGetParametersResponse | undefined;
    listData?: ListApiGetResponse;
    disabledInputFields: DisabledInputFields;
};

const Condition = ({
    condition,
    parameterData,
    isThresholdEditorOnly,
    overridesExist,
    index,
    listData,
    arrayHelper,
    disabledInputFields
}: ConditionProps) => {
    const { t } = useTranslation();
    const { parameterField, operatorField } = condition;
    const { sortedListsByType } = useGetSortedLists(listData, parameterField.type);
    const readOnly = isThresholdEditorOnly || overridesExist || disabledInputFields.disableDefinitionChange;
    const isParameterField = parameterField?.name[0] === undefined;
    const currentParameter = parameterData?.fieldNameHashmap[parameterField.name[0]];

    return (
        <div className={styles.conditionalStatementTextBar}>
            <div className={styles.conditionColumn}>
                <span className={styles.statementSideLabel}>{index === 0 ? t('WHEN') : t('AND')}</span>
            </div>
            <div className={styles.conditionColumn}>
                {readOnly ? (
                    <Popover
                        position='right-end'
                        showOnHover
                        showArrow
                        popperClassName={styles.popOverStyle}
                        contentClassName={styles.popOverContentStyle}
                        arrowClassName={styles.popperArrowStyle}
                        content={
                            <table className={styles.popOverTable}>
                                <tbody>
                                    <tr>
                                        <td>System Name</td>
                                        <td>{currentParameter?.fieldName ?? '_'}</td>
                                    </tr>
                                    <tr>
                                        <td>Display Name</td>
                                        <td>{currentParameter?.message ?? '_'}</td>
                                    </tr>
                                    <tr>
                                        <td>Type</td>
                                        <td>{currentParameter?.type ?? '_'}</td>
                                    </tr>
                                    <tr>
                                        <td>Description</td>
                                        <td>{currentParameter?.description ?? '_'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        }
                    >
                        <div className={styles.readOnlyValue}>
                            {parameterField?.displayName ?? parameterField.name[0] ?? '_'}
                        </div>
                    </Popover>
                ) : (
                    <ParameterField arrayHelper={arrayHelper} index={index} parameterData={parameterData} />
                )}
            </div>

            <div className={styles.conditionColumn}>
                {readOnly ? (
                    <div className={styles.readOnlyValue}>{t(`conditions.${operatorField[0]}`)}</div>
                ) : (
                    parameterField.name[0] && (
                        <OperatorField
                            arrayHelper={arrayHelper}
                            index={index}
                            parameterType={currentParameter?.parameterType}
                        />
                    )
                )}
            </div>

            <div className={styles.conditionColumn}>
                {parameterField.name[0] && operatorField[0] && listData && (
                    <ValueField
                        arrayHelper={arrayHelper}
                        index={index}
                        listData={listData}
                        parameterTyppType={currentParameter?.tppType}
                        sortedListsByType={sortedListsByType}
                        isThresholdEditorOnly={isThresholdEditorOnly}
                    />
                )}
            </div>

            <div className={styles.conditionColumn}>
                {!readOnly && (
                    <Field name={`${arrayHelper.name}[${index}].readOnly`}>
                        {({ form, field }: FieldProps) => {
                            return (
                                <Checkbox
                                    checked={field.value}
                                    className={styles.checkbox}
                                    onChange={(e) => {
                                        form.setFieldValue(field.name, e.target.checked);
                                        form.setFieldTouched(field.name, true);
                                    }}
                                    disabled={form.isSubmitting || isParameterField}
                                />
                            );
                        }}
                    </Field>
                )}
            </div>
        </div>
    );
};

export default React.memo(Condition);
